import React from 'react';
import ButtonInc from '../../../../../../shared-ui/ButtonInc';
import { getPartner } from '../../../../../../lib/access.es6';
import { formatPremium } from '../utils/calculations';
import { groupMembers, getPlanNumber } from '../utils/planHelpers';

const SummaryScreen = ({
  policy,
  originalMembers,
  updatedMembers,
  oldNuclearPlanCode,
  oldParentPlanCode,
  newNuclearPlanCode,
  newParentPlanCode,
  onBack,
  onConfirm
}) => {
  const currencyCode = getPartner().country.currency_code;

  // Calculate old and new totals
  const oldTotal = originalMembers.reduce((sum, m) => sum + (m.premium_amount_in_cents || 0), 0);
  const newTotal = updatedMembers.reduce((sum, m) => sum + (m.premium_amount_in_cents || 0), 0);
  const difference = newTotal - oldTotal;
  const isIncrease = difference > 0;

  const oldNuclearPlanNum = getPlanNumber(oldNuclearPlanCode);
  const oldParentPlanNum = getPlanNumber(oldParentPlanCode);
  const newNuclearPlanNum = getPlanNumber(newNuclearPlanCode);
  const newParentPlanNum = getPlanNumber(newParentPlanCode);

  // Group members for display
  const groupedUpdated = groupMembers(updatedMembers);

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '2px solid #e0e0e0'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ fontSize: '26px', fontWeight: 'bold', marginBottom: '8px', color: '#032A37' }}>
              Confirm Plan Change
            </h1>
            <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
              Review the changes below before confirming
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: '14px', color: '#666', margin: '0 0 4px 0' }}>
              <strong>Policy:</strong> {policy.policy_number}
            </p>
          </div>
        </div>
      </div>

      {/* Summary Box */}
      <div style={{
        backgroundColor: '#f5f5f5',
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '25px',
        marginBottom: '30px'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '20px', color: '#032A37' }}>
          Amendment Summary
        </h3>

        {/* Plan Changes */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '6px',
          padding: '15px',
          marginBottom: '15px'
        }}>
          <div style={{ marginBottom: '10px' }}>
            <span style={{ fontSize: '15px', fontWeight: '600' }}>Plan Changes:</span>
          </div>
          <div style={{ marginLeft: '15px' }}>
            <div style={{ marginBottom: '8px' }}>
              • Nuclear Plan: <strong>Plan {oldNuclearPlanNum} → Plan {newNuclearPlanNum}</strong>
            </div>
            <div>
              • Parent Plan: <strong>{oldParentPlanNum ? `Plan ${oldParentPlanNum}` : 'Not Set'} → Plan {newParentPlanNum}</strong>
            </div>
          </div>
        </div>

        {/* Premium Impact */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '6px',
          padding: '15px',
          marginBottom: '15px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ fontSize: '15px' }}>Current Total Premium:</span>
            <span style={{ fontSize: '16px', fontWeight: '600' }}>
              {currencyCode} {formatPremium(oldTotal)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ fontSize: '15px' }}>New Total Premium:</span>
            <span style={{ fontSize: '16px', fontWeight: '600' }}>
              {currencyCode} {formatPremium(newTotal)}
            </span>
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            paddingTop: '10px',
            borderTop: '2px solid #032A37'
          }}>
            <span style={{ fontSize: '15px', fontWeight: 'bold', color: '#032A37' }}>
              Premium {isIncrease ? 'Increase' : 'Decrease'}:
            </span>
            <span style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: isIncrease ? '#388e3c' : '#d32f2f'
            }}>
              {isIncrease ? '+' : ''}{currencyCode} {formatPremium(Math.abs(difference))}
            </span>
          </div>
        </div>

        {/* Members Table */}
        <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #ddd' }}>
          <p style={{ fontWeight: '600', marginBottom: '10px', fontSize: '16px' }}>
            All Members ({updatedMembers.length}):
          </p>

          {/* Nuclear Members */}
          {groupedUpdated.nuclear.length > 0 && (
            <div style={{ marginBottom: '15px' }}>
              <p style={{ fontWeight: '600', fontSize: '14px', marginBottom: '8px' }}>Nuclear Members:</p>
              <div style={{ maxHeight: '200px', overflowY: 'auto', marginLeft: '15px' }}>
                <table style={{
                  width: '100%',
                  fontSize: '13px',
                  borderCollapse: 'collapse'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#e0e0e0' }}>
                      <th style={{ padding: '6px', textAlign: 'left', borderBottom: '2px solid #ccc' }}>Name</th>
                      <th style={{ padding: '6px', textAlign: 'left', borderBottom: '2px solid #ccc' }}>Relationship</th>
                      <th style={{ padding: '6px', textAlign: 'right', borderBottom: '2px solid #ccc' }}>New Premium</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedUpdated.nuclear.map((member, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '6px' }}>{member.first_name} {member.last_name}</td>
                        <td style={{ padding: '6px' }}>{member.relationship}</td>
                        <td style={{ padding: '6px', textAlign: 'right' }}>
                          {currencyCode} {formatPremium(member.premium_amount_in_cents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Parent Members */}
          {groupedUpdated.parents.length > 0 && (
            <div style={{ marginBottom: '15px' }}>
              <p style={{ fontWeight: '600', fontSize: '14px', marginBottom: '8px' }}>Parent Members:</p>
              <div style={{ maxHeight: '200px', overflowY: 'auto', marginLeft: '15px' }}>
                <table style={{
                  width: '100%',
                  fontSize: '13px',
                  borderCollapse: 'collapse'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#e0e0e0' }}>
                      <th style={{ padding: '6px', textAlign: 'left', borderBottom: '2px solid #ccc' }}>Name</th>
                      <th style={{ padding: '6px', textAlign: 'left', borderBottom: '2px solid #ccc' }}>Relationship</th>
                      <th style={{ padding: '6px', textAlign: 'right', borderBottom: '2px solid #ccc' }}>New Premium</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedUpdated.parents.map((member, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '6px' }}>{member.first_name} {member.last_name}</td>
                        <td style={{ padding: '6px' }}>{member.relationship}</td>
                        <td style={{ padding: '6px', textAlign: 'right' }}>
                          {currencyCode} {formatPremium(member.premium_amount_in_cents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Extended Members */}
          {groupedUpdated.extended.length > 0 && (
            <div>
              <p style={{ fontWeight: '600', fontSize: '14px', marginBottom: '8px' }}>Extended Members:</p>
              <div style={{ maxHeight: '200px', overflowY: 'auto', marginLeft: '15px' }}>
                <p style={{ fontSize: '12px', fontStyle: 'italic', color: '#666', marginBottom: '8px' }}>
                  * Extended member benefits remain unchanged (independent of plan)
                </p>
                <table style={{
                  width: '100%',
                  fontSize: '13px',
                  borderCollapse: 'collapse'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#e0e0e0' }}>
                      <th style={{ padding: '6px', textAlign: 'left', borderBottom: '2px solid #ccc' }}>Name</th>
                      <th style={{ padding: '6px', textAlign: 'left', borderBottom: '2px solid #ccc' }}>Relationship</th>
                      <th style={{ padding: '6px', textAlign: 'right', borderBottom: '2px solid #ccc' }}>Premium</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedUpdated.extended.map((member, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '6px' }}>{member.first_name} {member.last_name}</td>
                        <td style={{ padding: '6px' }}>{member.relationship}</td>
                        <td style={{ padding: '6px', textAlign: 'right' }}>
                          {currencyCode} {formatPremium(member.premium_amount_in_cents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
        <ButtonInc
          btnType="secondary"
          onClick={onBack}
          style={{
            border: '1px solid #032A37'
          }}
        >
          Back
        </ButtonInc>
        <ButtonInc
          onClick={onConfirm}
          style={{
            background: '#032A37'
          }}
        >
          Confirm Plan Change
        </ButtonInc>
      </div>
    </div>
  );
};

export default SummaryScreen;
